import { Box, IconButton, styled, useMediaQuery } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import { useEffect, useRef, useState } from "react";
import ArrowLeftToLine from "icons/duotone/ArrowLeftToLine";
import MultiLevelMenu from "./MultiLevelMenu";
import MobileSidebar from "./MobileSidebar";

const TOP_HEADER_AREA = 70;

const NavScrollArea = styled(Box)(({ theme }) => ({
  flex: "1 1 0",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
  scrollbarColor:
    theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
  },
}));

// Anchor to top + bottom of the viewport so the sidebar always fills
// the full visible height — robust against root-level `zoom` scaling
// and any future layout changes.
const SidebarWrapper = styled(Box)(({
  theme,
  compact
}) => ({
  position: "fixed",
  top: 0,
  bottom: 0,
  left: 0,
  width: compact ? 86 : 280,
  display: "flex",
  flexDirection: "column",
  transition: "width .2s ease",
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  "&:hover": compact && {
    width: 280
  }
}));

const NavWrapper = styled(Box)(() => ({
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 16
}));

const LogoContainer = styled(FlexBox)(({ theme, compact }) => ({
  alignItems: "center",
  justifyContent: compact ? "center" : "flex-start",
  minWidth: compact ? "54px" : "auto",
  transition: "all 0.2s ease",
  overflow: "hidden"
}));

const LogoImage = styled("img")(({ theme, compact }) => ({
  height: compact ? "32px" : "40px",
  width: "auto",
  maxWidth: compact ? "32px" : "160px",
  objectFit: "contain",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)"
  }
}));

const StyledArrow = styled(ArrowLeftToLine)(() => ({
  display: "block"
}));

const StyledIconButton = styled(IconButton)(({
  theme
}) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

// -----------------------------------------------------------------------------
const DashboardSidebar = props => {
  const {
    sidebarCompact,
    showMobileSideBar,
    setShowMobileSideBar,
    setSidebarCompact
  } = props;
  const [onHover, setOnHover] = useState(false);
  const navScrollRef = useRef(null);
  const sidebarRef = useRef(null);
  const downLg = useMediaQuery(theme => theme.breakpoints.down("lg"));

  // Activate compact when toggle button clicked and not on hover state
  const COMPACT = sidebarCompact && !onHover ? 1 : 0;

  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    const navEl = navScrollRef.current;
    if (!sidebarEl || !navEl) return undefined;

    const handleWheel = (event) => {
      if (navEl.scrollHeight <= navEl.clientHeight) return;

      event.preventDefault();
      event.stopPropagation();

      const maxScrollTop = navEl.scrollHeight - navEl.clientHeight;
      navEl.scrollTop = Math.max(0, Math.min(maxScrollTop, navEl.scrollTop + event.deltaY));
    };

    sidebarEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => sidebarEl.removeEventListener("wheel", handleWheel);
  }, [downLg]);

  // IF MOBILE
  if (downLg) {
    return <MobileSidebar 
      sidebarCompact={!!COMPACT} 
      showMobileSideBar={!!showMobileSideBar} 
      setShowMobileSideBar={setShowMobileSideBar} 
    />;
  }

  return (
    <SidebarWrapper 
      ref={sidebarRef}
      compact={sidebarCompact ? 1 : 0} 
      onMouseEnter={() => setOnHover(true)} 
      onMouseLeave={() => sidebarCompact && setOnHover(false)}
    >
      <FlexBetween 
        pt={2} 
        pr={2} 
        pl={sidebarCompact && !onHover ? 2 : 3} 
        pb={1} 
        minHeight={TOP_HEADER_AREA}
        sx={{ flexShrink: 0 }}
      >
        {/* LOGO */}
        <LogoContainer compact={COMPACT}>
          <LogoImage 
            src="/nextgen/nextgen_logo.png"
            alt="NextGen Logo"
            compact={COMPACT}
          />
        </LogoContainer>

        {/* SIDEBAR COLLAPSE BUTTON */}
        {!COMPACT && (
          <StyledIconButton 
            onClick={setSidebarCompact}
            sx={{ ml: "auto" }}
          >
            <StyledArrow />
          </StyledIconButton>
        )}
      </FlexBetween>

      {/* NAVIGATION ITEMS — independent scroll area */}
      <NavScrollArea ref={navScrollRef}>
        <NavWrapper>
          <MultiLevelMenu sidebarCompact={!!COMPACT} />
        </NavWrapper>
      </NavScrollArea>
    </SidebarWrapper>
  );
};

export default DashboardSidebar;